<script setup>
import { computed, onMounted, ref } from 'vue';
import { PhxIcon, PhxLightbox, slider } from '@phx-styleguides/iwc';
import ImageSliderItem from '@maison/components/organisms/slider/ImageSliderItem.vue';
import { set } from 'lodash-es';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  lightbox: {
    type: Object,
    default: null,
  },
  isFullWidth: {
    type: Boolean,
    default: false,
  },
  isThumbnails: {
    type: Boolean,
    default: false,
  },
  thumbnailsTeleportTarget: {
    type: String,
    default: '',
  },
  classes: {
    type: String,
    default: '',
  },
  thumbnailsClasses: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const sliderRef = ref(null);
const sliderInstance = ref(null);

const thumbnailsSliderRef = ref(null);
const thumbnailsSliderInstance = computed(() => thumbnailsSliderRef.value?.sliderInstance);

const sliderClasses = computed(() => [
  'slider',
  props.classes,
  {
    'slider--full-width': props.isFullWidth,
    'slider--thumbnails': props.isThumbnails,
  },
]);

const hasLightbox = computed(() => props.lightbox !== null);

const lightboxOptions = computed(() => ({
  ...props.lightbox,
  loop: false,
  counter: true,
  thumbnails: {
    enabled: true,
    options: {
      centerInsufficientSlides: true,
      slidesPerView: 4,
      mousewheel: {
        forceToAxis: true,
      },
      breakpoints: {
        768: {
          direction: 'vertical',
        },
      },
    },
    a11y: {
      label: t('Accessibility.lightbox.thumbnailsLabel'),
    },
  },
}));

const thumbnails = computed(() => (props.isThumbnails ? [] : props.items.map((item) => item.thumbnail)));

const hasThumbnails = computed(() => thumbnails.value.length > 0);

const sliderOptions = computed(() => {
  const specificOptions = props.isThumbnails
    ? {
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centerInsufficientSlides: true,
        breakpoints: {
          768: {
            centerInsufficientSlides: false,
          },
        },
      }
    : {
        slidesPerView: 1,
        effect: 'fade',
      };

  const options = {
    threshold: 5,
    mousewheel: {
      forceToAxis: true,
    },
    ...specificOptions,
  };

  if (thumbnailsSliderInstance.value) {
    set(options, 'thumbs.swiper', thumbnailsSliderInstance.value);
  }

  return options;
});

const handleLightboxChange = (index) => {
  if (sliderInstance.value) {
    sliderInstance.value.slideTo(index);
  }
};

onMounted(() => {
  if (sliderRef.value) {
    sliderInstance.value = slider(sliderRef.value, sliderOptions.value);
  }
});

defineExpose({
  sliderInstance,
});
</script>

<template>
  <div
    :id="id"
    ref="sliderRef"
    :class="sliderClasses"
    role="group"
    aria-roledescription="carousel"
    :aria-label="t('Accessibility.imageSlider.ariaLabel')"
  >
    <div class="slider__nav slider__nav--prev">
      <button type="button" class="btn btn--icon btn--icon-only btn--sm btn--icon btn--icon-only btn--plain">
        <span class="btn__icon btn__icon--start">
          <phx-icon icon="chevron-left" bidirectional class="icon--lg" />
        </span>
      </button>
    </div>

    <div class="slider__body" aria-atomic="false" aria-live="polite">
      <image-slider-item
        v-for="(item, index) in items"
        :id="item.id"
        :key="index"
        :image="item.image"
        :index="index"
        :picture-classes="item.pictureClasses"
        :lightbox-id="lightbox?.id ?? null"
      />
    </div>

    <div class="slider__nav slider__nav--next">
      <button type="button" class="btn btn--icon btn--icon-only btn--sm btn--icon btn--icon-only btn--plain">
        <span class="btn__icon btn__icon--start">
          <phx-icon icon="chevron-right" bidirectional class="icon--lg" />
        </span>
      </button>
    </div>
  </div>

  <teleport v-if="hasThumbnails" :to="`#${thumbnailsTeleportTarget}`">
    <image-slider
      :id="`${id}-thumbnails`"
      ref="thumbnailsSliderRef"
      :items="thumbnails"
      :classes="thumbnailsClasses"
      is-thumbnails
    />
  </teleport>

  <phx-lightbox v-if="hasLightbox" v-bind="lightboxOptions" @change="handleLightboxChange" />
</template>
